// Ported from eb-ui `src/legacy/Collection/CollectionDetails.tsx`.
//   - `@eventbrite/eds-avatar` → Marmalade `Avatar` (`imageSrc`, `size="large"`
//     — the eds default was 80px).
//   - `@eventbrite/legacy-infra.i18n` `gettext` → `@/lib/gettext`.
//   - context → the local ported `../context` (ApplicationContext).
import { Avatar } from '@eventbrite/marmalade';
import React, { useContext } from 'react';

import { AnalyticsAction, AnalyticsCategory, trackEvent } from '../_shared';
import { getDefaultAutocreatedCollectionSummary } from '../_shared/constants';
import {
    isAutocreatedCollectionPreviewWithTips,
    isAutocreatedCollectionUnedited,
} from '../_shared/utils';
import { ApplicationContext } from '../context';
import { gettext } from '@/lib/gettext';
import type { CollectionServerData } from '@/types';

export type CollectionDetailsProps = {
    collectionName: string;
    collectionSummary?: string;
    organizerName?: string;
    organizerUrl?: string;
    organizerLogoUrl?: string;
    organizerFollowersCount?: number;
};

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
    collectionName,
    collectionSummary,
    organizerName,
    organizerUrl,
    organizerLogoUrl,
    organizerFollowersCount = 0,
}) => {
    const minFollowersNumber = 10;
    const appContext = useContext(ApplicationContext) as
        | CollectionServerData
        | null;
    const summary =
        isAutocreatedCollectionUnedited(appContext?.collection) &&
        isAutocreatedCollectionPreviewWithTips(appContext)
            ? getDefaultAutocreatedCollectionSummary()
            : collectionSummary;

    return (
        <>
            <div className="cc-signifier eds-text-bl">
                {gettext('Collection')}
            </div>

            <h1 className="cc-title eds-text-hl">{collectionName}</h1>

            <div className="cc-organizer-snippet eds-l-mar-bot-8">
                {organizerLogoUrl && (
                    <div className="cc-organizer-snippet__avatar">
                        <Avatar imageSrc={organizerLogoUrl} size="large" />
                    </div>
                )}
                {organizerName && organizerUrl && (
                    <div className="cc-organizer-snippet__info">
                        <div className="eds-text-bm eds-text-weight--bold">
                            <a
                                href={organizerUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => {
                                    trackEvent({
                                        action: AnalyticsAction.OrganizerName,
                                        category: AnalyticsCategory.Listing,
                                        label: 'organizer-details',
                                    });
                                }}
                            >
                                {organizerName}
                            </a>
                        </div>
                        {organizerFollowersCount >= minFollowersNumber && (
                            <div className="eds-text-sm eds-text-color--ui-600">
                                {organizerFollowersCount} {gettext('followers')}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="cc-main-description eds-l-mar-bot-8">
                <p className="eds-text-bl">{summary}</p>
            </div>
        </>
    );
};
